import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { evidenceStorageLocationsApi } from '../evidenceApi';
import type { EvidenceStorageLocationListItem } from '../evidenceTypes';
import { sampleStorageLocation } from '../utils/sampleEvidence';

/** Creates or edits an evidence storage location. The CODE is allocated and never on the form. */
export function EvidenceStorageLocationModal({
  editing,
  onClose,
  onSaved,
}: {
  editing: EvidenceStorageLocationListItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = name.trim().length > 0 && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = { name: name.trim(), description: description.trim() || null };

    try {
      if (editing) await evidenceStorageLocationsApi.update(editing.id, request);
      else await evidenceStorageLocationsApi.create(request);

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the location.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'evidence-storage-location' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? `Edit ${editing.code}` : 'New storage location'}
      size="lg"
      id="evidence-storage-location-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleStorageLocation();
          setName(sample.name);
          setDescription(sample.description);
        },
        disabled: saving,
      }}
      confirm={{ id: 'evidence-storage-location-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="evidence-storage-location-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="evidence-storage-location-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="evidence-storage-location-name"
          label="Name"
          required
          description="What the room, vault or cabinet is called. Unique among live locations. A cabinet inside a room is written into the name — this list is flat."
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={150}
          disabled={saving}
          error={error?.fieldError('name')}
        />

        <Textarea
          id="evidence-storage-location-description"
          label="Description"
          description="Optional. Where it is and how it is secured."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={1000}
          disabled={saving}
          error={error?.fieldError('description')}
        />
      </div>
    </Modal>
  );
}
